import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { transform } from 'ol/proj';

import LaunchIcon from '@mui/icons-material/Launch';

import TableButton from './TableButton';
import Permissions from '@/lib/permissions';
import useApi from '@/lib/api/useApi';

import { ITableActionButtonDetail } from '@/@types/ui/Table';
import { DCRecord } from '@/@types/lib/dataController';
import UserContext from '@/context/UserContext/UserContext';

interface ITableButtonShow3D extends Omit<ITableActionButtonDetail, 'onClick'> {
  selection?: { [key: string]: DCRecord };
  onClick?: (evt: React.SyntheticEvent, selection: { [key: string]: DCRecord }) => void;
}

const TableButtonShow3D = (props: ITableButtonShow3D) => {
  const { t } = useTranslation();
  const { selection = {}, ...rest } = props;
  const apiInstance = useApi();

  const [isIn3D, setIsIn3D] = useState(false);

  const selectedRecord = Object.values(selection)[0] as DCRecord | undefined;

  const userContext = useContext(UserContext);

  const hasPermission3D = userContext?.hasAnyPermission([Permissions.ThreeDView]);

  useEffect(() => {


    if (!selectedRecord?.cx || !selectedRecord?.cy || !hasPermission3D) {
      setIsIn3D(false);
      return;
    }

    const [x, y] = transform([selectedRecord.cx as number, selectedRecord.cy as number], 'EPSG:3765', 'EPSG:3857');

    apiInstance
      .get(`threeD/is-in-3d?x=${x}&y=${y}`)
      .then((resp) => {
        if (resp.success && resp.data) {
          setIsIn3D(!!(resp.data as DCRecord).inside);
        } else {
          setIsIn3D(false);
        }
      })
      .catch(() => setIsIn3D(false));
  }, [selectedRecord?.cx, selectedRecord?.cy]);

  const handleClick = () => {
    if (!selectedRecord?.cx || !selectedRecord?.cy) return;

    const [cx, cy] = transform([selectedRecord.cx as number, selectedRecord.cy as number], 'EPSG:3765', 'EPSG:4326').map(
      (x) => (x * Math.PI) / 180
    );

    const url = `/3d?cx=${cx}&cy=${cy}&cz=${selectedRecord.cz ?? 0}`;
    window.open(url, '_blank');
  };

  return hasPermission3D ? (
    <TableButton
      allow={isIn3D ? 'one' : 'none'}
      selection={selection}
      onClick={handleClick}
      needPermission={[Permissions.ThreeDView]}
      startIcon={<LaunchIcon />}
      variant="outlined"
      {...rest}
    >
      {t('buttons.show_3d')}
    </TableButton>
  ) : null;
};

export default TableButtonShow3D;
